import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Zap, Edit, Users, FileText, Download, Shield, Layers, Calculator, Ruler, DollarSign, Share2, Droplets, Check, ArrowRight, MapPin, Clock } from 'lucide-react';

export default function Features() {
  const { openAuthModal } = useAuth();

  // 3-step workflow
  const steps = [
    { icon: MapPin, title: '1. Enter an address', text: 'Type any U.S. address. We pull high-resolution satellite imagery and you trace the roof — or let the facets auto-detect.' },
    { icon: Ruler, title: '2. Get the measurements', text: 'Pitch-adjusted area, squares, and perimeter in about 60 seconds. Add multiple sections, valleys, and ridges for a full takeoff.' },
    { icon: FileText, title: '3. Build the list & price the job', text: 'Pick shingle, tile, or metal and the material list builds itself. Edit quantities, add custom items, price the whole job, and send a clean report to the homeowner.' },
  ];

  // Deep-dive sections for the headline capabilities
  const deepDives = [
    {
      icon: Zap,
      tier: 'Free',
      title: 'Instant satellite measurements',
      text: 'Measure any roof from your desk in about 60 seconds — no ladder, no on-site visit, no 24-48 hour wait for a report to come back.',
      points: [
        'High-resolution satellite imagery for any U.S. address',
        'Pitch-adjusted area, squares, and perimeter',
        'Manual pitch entry, or set it per section',
        'Multi-section roofs: measure each facet and combine the totals (Pro)',
      ],
    },
    {
      icon: Layers,
      tier: 'Contractor',
      title: 'Shingle, tile & metal material lists',
      text: 'Pick the roofing material and the whole list rebuilds for it — the right products in the right units. Tiles don’t come in bundles, so we don’t quote them that way.',
      points: [
        'Asphalt shingle — bundles, squares, ridge cap, starter, underlayment, ice & water, drip edge',
        'Tile — counted by the piece, with the field/hip/ridge pieces broken out',
        'Metal — panels and trim by the linear foot for the roof you measured',
        'Switch material types and recalculate without re-measuring',
      ],
    },
    {
      icon: Ruler,
      tier: 'Contractor',
      title: 'Valley & ridge takeoffs',
      text: 'Draw the valleys and ridges right on the roof and get the linear footage on every report, PDF, and CSV — so your valley metal and ridge cap are quoted off real numbers, not a guess.',
      points: [
        'Draw valley and ridge lines directly on the satellite image',
        'Color-coded so they stay separate from the roof area',
        'Linear footage flows onto the report, PDF, and CSV export',
        'Reference measurements — they never change your roof area total',
      ],
    },
    {
      icon: Edit,
      tier: 'Contractor',
      title: 'Editable reports & custom materials',
      text: 'No two jobs are identical. Change any quantity and add your own line items — pipe boots, ridge vent, skylight flashing, step flashing, whatever the job needs.',
      points: [
        'Edit any material quantity on the report',
        'Add unlimited custom line items with your own units',
        'Adjust pitch or waste factor and recalculate instantly',
        'Custom items carry through to the PDF, CSV, and client report',
      ],
    },
    {
      icon: DollarSign,
      tier: 'Contractor',
      title: 'Job cost estimator',
      text: 'Turn a material list into a priced job. Add your material costs plus labor, dumpster, and permits to see the whole number before you quote. Private to you — it never shows on the client report.',
      points: [
        'Price every material line at your real cost',
        'Add labor, dumpster, permits, and any custom cost lines',
        'See the full job total and your margin in one place',
        'Stays internal — the homeowner never sees your costs',
      ],
    },
    {
      icon: Share2,
      tier: 'Contractor',
      title: 'Shareable, white-label client reports',
      text: 'Send the homeowner a clean, branded report link — no login required for them. Put your logo, company info, and colors on it and remove SquareCount branding entirely.',
      points: [
        'Share a live report link straight to the customer',
        'Your logo, company name, phone, and colors on every report',
        'White-label — remove SquareCount branding on Contractor',
        'Add your crew with their own logins and shared access',
      ],
    },
  ];

  // Full quick-reference grid
  const features = [
    { icon: Zap, title: 'Instant Satellite Measurements', description: 'Accurate roof measurements in about 60 seconds. No ladders, no 24-48 hour wait.', tier: 'Free' },
    { icon: Calculator, title: 'Material Estimates', description: 'Automatic material list on every measurement — shingles, underlayment, decking, ice & water, drip edge, ridge cap.', tier: 'Free' },
    { icon: FileText, title: 'PDF Reports', description: 'Download polished, customer-ready PDF reports with all measurements and material lists.', tier: 'Pro' },
    { icon: Droplets, title: 'Gutter Estimates', description: 'Add gutter materials from the roof perimeter with a single toggle.', tier: 'Pro' },
    { icon: Layers, title: 'Shingle, Tile & Metal', description: 'Pick the roof material and the list and units adjust — tiles by the piece, metal by the panel, shingles by the bundle.', tier: 'Contractor' },
    { icon: Ruler, title: 'Valley & Ridge Takeoffs', description: 'Draw valleys and ridges and get the linear footage on your report, PDF, and CSV.', tier: 'Contractor' },
    { icon: Edit, title: 'Editable Reports & Custom Materials', description: 'Edit any quantity and add your own line items — anything the job needs.', tier: 'Contractor' },
    { icon: DollarSign, title: 'Job Cost Estimator', description: 'Price materials and add labor, dumpster, and permits to cost the whole job. Private to you.', tier: 'Contractor' },
    { icon: Share2, title: 'Shareable Client Reports', description: 'Send a clean, branded report link to the homeowner — no login required for them.', tier: 'Contractor' },
    { icon: Download, title: 'CSV Export', description: 'Export the full material list as CSV for direct ordering from suppliers.', tier: 'Contractor' },
    { icon: Users, title: 'Team Members', description: 'Add your crew with their own logins and shared measurement access.', tier: 'Contractor' },
    { icon: Shield, title: 'White-Label & Branding', description: 'Put your logo, company info, and colors on every report. Remove SquareCount branding.', tier: 'Contractor' },
  ];

  // Tier comparison
  const tiers = [
    { name: 'Free', price: '$0', blurb: 'Measure and view.', items: ['3 measurements / month', 'Satellite measurements', 'Pitch-adjusted area & squares', 'On-screen material estimate', 'View-only reports'] },
    { name: 'Pro', price: '$19/mo', blurb: 'Unlimited shingle reports.', items: ['Everything in Free', 'Unlimited measurements', 'PDF report downloads', 'Gutter estimates', 'Multi-section roofs', 'Cloud history & storage'] },
    { name: 'Contractor', price: '$79/mo', blurb: 'The full toolkit.', popular: true, items: ['Everything in Pro', 'Shingle, tile & metal lists', 'Valley & ridge takeoffs', 'Editable reports & custom materials', 'Job cost estimator', 'Shareable & white-label reports', 'CSV export', 'Team members'] },
  ];

  const faqs = [
    { q: 'How accurate are the measurements?', a: 'SquareCount delivers fast, estimate-grade measurements from satellite imagery — roof area, pitch, squares, perimeter, and valley/ridge footage. Always verify final quantities on-site before ordering material.' },
    { q: 'Do I need a credit card to try it?', a: 'No. Every new account starts with a free 7-day trial of the full Contractor plan. No card up front — it reverts to Free automatically when the trial ends if you don’t upgrade.' },
    { q: 'Can I measure tile and metal roofs, not just shingle?', a: 'Yes, on Contractor. Pick the material and the list rebuilds — tile counted by the piece, metal by the panel and trim, shingle by the bundle. Switch material and recalculate without re-measuring.' },
    { q: 'Will my customer see my costs?', a: 'No. The job cost estimator (your material costs, labor, dumpster, permits) is private to you. The shareable client report shows measurements and the material list — never your pricing.' },
    { q: 'What happens to reports I already made if my plan changes?', a: 'You keep full access to reports you created under Contractor access, even after a trial or report-pack credits run out. Only new report generation is gated by your current plan.' },
  ];

  const tierBadge = (t) => (
    <span className={`text-xs font-bold px-2 py-1 rounded ${t === 'Contractor' ? 'bg-orange-500/20 text-orange-400' : t === 'Pro' ? 'bg-sky-500/20 text-sky-400' : 'bg-slate-800 text-slate-400'}`}>{t}</span>
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Features | Shingle, Tile &amp; Metal Roof Measurements + Job Costing | SquareCount</title>
        <meta name="description" content="Satellite roof measurements with editable material lists for shingle, tile, and metal. Valley &amp; ridge takeoffs, custom materials, job cost estimator, shareable white-label client reports, CSV export, and team accounts. See everything SquareCount does." />
        <link rel="canonical" href="https://squarecount.io/features" />
        <meta property="og:title" content="SquareCount Features — Roof Measurements, Material Lists &amp; Job Costing" />
        <meta property="og:description" content="Editable material lists for shingle, tile &amp; metal, valley/ridge takeoffs, a built-in job cost estimator, and shareable client reports." />
        <meta property="og:url" content="https://squarecount.io/features" />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mb-6">
            Measure Any Roof. Build the Material List. Price the Job.
          </h1>
          <p className="text-xl text-slate-300 mb-8">
            From a satellite measurement to a priced, client-ready report — for shingle, tile, and metal.
            Here's everything SquareCount does, and which plan it's on.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold" onClick={() => openAuthModal('register')}>
              Start Your 7-Day Contractor Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="font-heading font-bold border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-heading font-bold text-3xl text-white text-center mb-3">How it works</h2>
        <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">Three steps from an address to a quote you can send.</p>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-7 h-7 text-orange-400" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-2">{s.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Deep-dive feature sections */}
      <section className="bg-slate-900/40 border-y border-slate-800 py-16">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <h2 className="font-heading font-bold text-3xl text-white text-center mb-2">What you can do</h2>
          <p className="text-slate-400 text-center -mt-8 mb-4">The capabilities, in detail.</p>
          {deepDives.map((d, i) => {
            const Icon = d.icon;
            return (
              <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading font-bold text-2xl text-white">{d.title}</h3>
                      {tierBadge(d.tier)}
                    </div>
                    <p className="text-slate-300 leading-relaxed">{d.text}</p>
                  </div>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-4 sm:pl-16">
                  {d.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Full feature grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-heading font-bold text-3xl text-white text-center mb-3">Everything you get</h2>
        <p className="text-slate-400 text-center mb-12">The full list, with the plan each feature is on.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="bg-slate-900/50 border border-slate-800 hover:border-orange-500/30 rounded-lg transition-all p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-orange-400" />
                  </div>
                  {tierBadge(feature.tier)}
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Tier comparison */}
      <section className="bg-slate-900/40 border-y border-slate-800 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl text-white text-center mb-3">What's on each plan</h2>
          <p className="text-slate-400 text-center mb-12">Free to start. Contractor for the full toolkit.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <div key={t.name} className={`rounded-2xl border p-6 ${t.popular ? 'border-orange-500/50 bg-orange-500/[0.04]' : 'border-slate-800 bg-slate-900/50'}`}>
                {t.popular && <div className="text-xs font-bold uppercase text-orange-400 mb-2">Most popular</div>}
                <h3 className="font-heading font-bold text-xl text-white">{t.name}</h3>
                <div className="text-3xl font-heading font-bold text-white mt-1 mb-1">{t.price}</div>
                <p className="text-slate-400 text-sm mb-4">{t.blurb}</p>
                <ul className="space-y-2">
                  {t.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${t.popular ? 'text-orange-400' : 'text-green-400'}`} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing">
              <Button variant="outline" className="font-heading font-bold border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                See full pricing &amp; comparison
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="font-heading font-bold text-3xl text-white text-center mb-10">Common questions</h2>
        <div className="space-y-6">
          {faqs.map((f, i) => (
            <div key={i} className="border-b border-slate-800 pb-6">
              <h3 className="font-heading font-bold text-lg text-white mb-2">{f.q}</h3>
              <p className="text-slate-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-20 text-center">
        <h2 className="font-heading font-bold text-3xl text-white mb-4">Try the whole toolkit free for 7 days</h2>
        <p className="text-slate-400 mb-8 max-w-2xl mx-auto">Full Contractor access — shingle, tile &amp; metal, valley/ridge, editable reports, job costing, and shareable client reports. No credit card.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" className="bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold" onClick={() => openAuthModal('register')}>
            Start Your 7-Day Contractor Trial
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Link to="/pricing">
            <Button size="lg" variant="outline" className="font-heading font-bold border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
              View Pricing
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
